import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'

import { RowsWrapper, RowWrapper, RoleCell, StyledCell, StyledTooltip } from './styles'
import { BaseRolesConstant } from '../constants'
import { BqDivider, BqIcon, BqButton, BqOption, BqSpinner } from '@beeq/react'
import { columnAndRowsWidth } from '..'
import { ActionsMenu } from '../../Base/ActionsMenu'

import { isSystemRole } from '../../../utils/utils'

import { Text } from '../../../constants/TextConstants'

export const TableRows = ({ isLoading, displayedRoles, disableActions, onViewRole, onEditRole, onDeleteRole }) => {
  const rowsRef = useRef()
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false)

  useEffect(() => {
    const checkScrollbar = () => {
      const rowsWrapper = rowsRef.current
      if (rowsWrapper) {
        const hasVertical = rowsWrapper.scrollHeight > rowsWrapper.clientHeight
        setHasVerticalScrollbar(hasVertical)
      }
    }

    checkScrollbar()
    window.addEventListener('resize', checkScrollbar)

    return () => window.removeEventListener('resize', checkScrollbar)
  }, [])

  if (isLoading) {
    return (
      <BqSpinner size='large' text-position='bellow' style={{ top: '100px' }} data-testid='spinner-role-manager'>
        <span>{Text.GenericLoading}</span>
      </BqSpinner>
    )
  }

  return (
    <RowsWrapper ref={rowsRef}>
      {displayedRoles.map((role) => (
        <React.Fragment key={role._id}>
          <RowWrapper>
            <RoleCell className='caption medium' $width={columnAndRowsWidth.roles}>
              <span data-testid={`role-${role.name}`}>{role.name}</span>
              {isSystemRole(role.name) && (
                <StyledTooltip placement='right'>
                  {BaseRolesConstant[role.name]}
                  <BqButton slot='trigger' variant='ghost' appearance='text' size='small'>
                    <BqIcon
                      name='info'
                      weight='bold'
                      color='icon--brand'
                      size='24'
                      data-testid={`role-${role.name}-info-icon`}
                    />
                  </BqButton>
                </StyledTooltip>
              )}
            </RoleCell>
            <StyledCell $width={columnAndRowsWidth.users}>
              <span data-testid={`user-${role.name}-amount-${role.assignedUsersCount}`}>{role.assignedUsersCount}</span>
            </StyledCell>
            <StyledCell $width={columnAndRowsWidth.actions} $hasTableVerticalScrollbar={hasVerticalScrollbar}>
              <ActionsMenu panelHeight={152} placement='bottom-end'>
                <>
                  <BqOption value='ViewRoleDetails' onBqClick={() => onViewRole(role._id)}>
                    <BqIcon slot='prefix' name='eye' />
                    <span>{Text.ViewRoleTitle}</span>
                  </BqOption>
                  {disableActions(role.name) && (
                    <>
                      <BqOption
                        value='EditRolePermission'
                        onBqClick={() => onEditRole(role._id)}
                        data-testid='actions-column-edit-option'>
                        <BqIcon slot='prefix' name='pencil-simple' />
                        <span>{Text.EditRolePermissions}</span>
                      </BqOption>
                      <BqOption
                        value='DeleteRole'
                        onBqClick={() => onDeleteRole(role._id)}
                        data-testid='actions-column-delete-option'>
                        <BqIcon slot='prefix' name='trash' />
                        <span>{Text.DeleteRole}</span>
                      </BqOption>
                    </>
                  )}
                </>
              </ActionsMenu>
            </StyledCell>
          </RowWrapper>
          <BqDivider strokeColor='ui--secondary' strokeThickness={1} />
        </React.Fragment>
      ))}
    </RowsWrapper>
  )
}

TableRows.propTypes = {
  disableActions: PropTypes.func,
  onViewRole: PropTypes.func,
  onEditRole: PropTypes.func,
  onDeleteRole: PropTypes.func,
  displayedRoles: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      assignedUsersCount: PropTypes.number.isRequired,
      systemRoleOrder: PropTypes.number
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired
}
